import { config } from './env';

const CONFIG_PATH = '/config.js';
const VERSION_QUERY_PARAM = '__shell_refresh';
const RELOAD_GUARD_KEY = 'shell-runtime-version-refresh';

type SessionStorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
type HistoryLike = Pick<History, 'replaceState'>;

export const normalizeVersion = (value: string | null | undefined): string | null => {
  const normalized = value?.trim();
  return normalized ? normalized : null;
};

export const parseRuntimeVersion = (configScript: string): string | null => {
  const match = configScript.match(/APP_VERSION:\s*['"]([^'"]+)['"]/);
  return normalizeVersion(match?.[1]);
};

export const isBundleStale = (buildVersion: string | null | undefined, runtimeVersion: string | null | undefined) => {
  const currentBuildVersion = normalizeVersion(buildVersion);
  const currentRuntimeVersion = normalizeVersion(runtimeVersion);

  return Boolean(currentBuildVersion && currentRuntimeVersion && currentBuildVersion !== currentRuntimeVersion);
};

const buildRefreshUrl = (href: string, runtimeVersion: string) => {
  const nextUrl = new URL(href);
  nextUrl.searchParams.set(VERSION_QUERY_PARAM, runtimeVersion);
  return nextUrl.toString();
};

const clearRefreshParam = (history: HistoryLike, href: string) => {
  const currentUrl = new URL(href);
  if (!currentUrl.searchParams.has(VERSION_QUERY_PARAM)) {
    return;
  }

  currentUrl.searchParams.delete(VERSION_QUERY_PARAM);
  history.replaceState({}, '', `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
};

export const fetchRuntimeVersion = async (fetchImpl: typeof fetch = window.fetch.bind(window)) => {
  try {
    const response = await fetchImpl(`${CONFIG_PATH}?ts=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
      },
    });

    if (!response.ok) {
      return null;
    }

    return parseRuntimeVersion(await response.text());
  } catch {
    return null;
  }
};

interface VersionSyncDependencies {
  buildVersion?: string;
  fetchImpl?: typeof fetch;
  history?: HistoryLike;
  href?: string;
  replaceLocation?: (url: string) => void;
  sessionStorage?: SessionStorageLike;
}

export const syncShellBundleVersion = async ({
  buildVersion = config().version,
  fetchImpl = window.fetch.bind(window),
  history = window.history,
  href = window.location.href,
  replaceLocation = (url) => window.location.replace(url),
  sessionStorage = window.sessionStorage,
}: VersionSyncDependencies = {}) => {
  const runtimeVersion = await fetchRuntimeVersion(fetchImpl);

  if (!runtimeVersion || !isBundleStale(buildVersion, runtimeVersion)) {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
    clearRefreshParam(history, href);
    return false;
  }

  const guardValue = `${buildVersion}->${runtimeVersion}`;
  if (sessionStorage.getItem(RELOAD_GUARD_KEY) === guardValue) {
    return false;
  }

  sessionStorage.setItem(RELOAD_GUARD_KEY, guardValue);
  replaceLocation(buildRefreshUrl(href, runtimeVersion));
  return true;
};

export const registerShellVersionSync = () => {
  const handlePageShow = (event: PageTransitionEvent) => {
    if (event.persisted) {
      void syncShellBundleVersion();
    }
  };

  window.addEventListener('pageshow', handlePageShow);

  return () => {
    window.removeEventListener('pageshow', handlePageShow);
  };
};
